import { HttpClient } from '@angular/common/http';
import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { S3UploadService } from 'gung-common';
import { first, forkJoin, map, Observable, of } from 'rxjs';
import { FeatureActivatedGuardWrapper } from '../../guards/feature-activated/feature-activated.guard';
import { S3Image } from '../../models/s3';
import { AuthService } from '../../services/auth/auth.service';
import { GungCompany, GungCompanyService } from '../../services/gung-company/gung-company.service';
import { gungColorToHsl } from '../../utils/gung-utils';
import { GungSettingsBaseViewComponent } from '../gung-settings-base-view/gung-settings-base-view.component';
import { OptionsList } from 'gung-common';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'lib-my-company',
  templateUrl: './my-company.component.html',
  styleUrls: ['./my-company.component.scss'],
  standalone: false
})
export class MyCompanyComponent extends GungSettingsBaseViewComponent implements OnInit {
  protected formBuilder = inject(FormBuilder);
  protected companyService = inject(GungCompanyService);
  protected authService = inject(AuthService);
  protected http = inject(HttpClient);
  protected featureActivatedGuardWrapper = inject(FeatureActivatedGuardWrapper);
  protected s3UploadService = inject(S3UploadService);
  protected translateService = inject(TranslateService);

  public form: FormGroup;
  myCompany: GungCompany;

  public objectFileImage: { [s: string]: File } = {};
  public objectFileImagePreview: { [s: string]: S3Image } = {};

  public objectFileFonts: { [s: string]: File } = {};
  public objectFileFontsPreview: { [s: string]: S3Image } = {};

  selectedNavItem: string;

  primary: string;
  secondary: string;
  hover: string;
  contrasting: string;
  backendVersion402: boolean;

  textFormattingOptionList: OptionsList[] = [
    { id: '', name: '' },
    { id: 'bold', name: this.translateService.instant('BOLD') },
    { id: 'italic', name: this.translateService.instant('ITALIC') },
    { id: 'link', name: this.translateService.instant('LINK') },
  ];

  bannerHeightOptionList: OptionsList[] = [
    { id: '40', name: this.translateService.instant('SMALL') },
    { id: '60', name: this.translateService.instant('MEDIUM') },
    { id: '80', name: this.translateService.instant('LARGE') },
  ];

  ngOnInit(): void {
    this.authService.getCurrentUser().subscribe(user => { });
    this.companyService.getGungCompany().subscribe(company => {
      this.myCompany = company;
      this.initForm();
      this.objectFileImagePreview = {
        loginBackgroundImage: this.myCompany?.loginBackgroundImage?.s3Uri && this.myCompany?.loginBackgroundImage,
        logo: this.myCompany?.logo?.s3Uri && this.myCompany?.logo,
        logoLogin: this.myCompany?.logoLogin?.s3Uri && this.myCompany?.logoLogin,
        logoOrderConfirm: this.myCompany?.logoOrderConfirm?.s3Uri && this.myCompany?.logoOrderConfirm,
        favicon: (this.myCompany?.favicon?.s3Uri && this.myCompany?.favicon) || this.myCompany?.extra?.favicon,
        'extra.navbarBanner.navbarBannerImage': this.myCompany?.extra?.navbarBanner?.navbarBannerImage
      };
      this.objectFileFontsPreview = {
        WOFF2: this.myCompany?.extra?.WOFF2?.s3Uri,
        WOFF: this.myCompany?.extra?.WOFF?.s3Uri,
        TTF: this.myCompany?.extra?.TTF?.s3Uri,
      };
    });
    this.primary = document.documentElement.style.getPropertyValue('--dynamic-colour-primary');
    this.secondary = document.documentElement.style.getPropertyValue('--dynamic-colour-secondary');
    this.hover = document.documentElement.style.getPropertyValue('--dynamic-colour-hover');
    this.contrasting = document.documentElement.style.getPropertyValue('--dynamic-colour-contrasting');
    this.featureActivatedGuardWrapper.isActivated('gung', '4.0.2').pipe(first()).subscribe((isActivated: boolean) => this.backendVersion402 = isActivated);
  }

  initForm() {
    this.form = this.formBuilder.group({
      name: this.formBuilder.control({ value: this.myCompany?.name || undefined, disabled: false }, [
        Validators.required
      ]),
      address1: this.formBuilder.control({ value: this.myCompany?.address1 || undefined, disabled: false }),
      address2: this.formBuilder.control({ value: this.myCompany?.address2 || undefined, disabled: false }),
      zipCode: this.formBuilder.control({ value: this.myCompany?.zipCode || undefined, disabled: false }),
      city: this.formBuilder.control({ value: this.myCompany?.city || undefined, disabled: false }),
      country: this.formBuilder.control({ value: this.myCompany?.country || undefined, disabled: false }),
      organizationNumber: this.formBuilder.control({
        value: this.myCompany?.organizationNumber || undefined,
        disabled: false
      }),
      email: this.formBuilder.control({ value: this.myCompany?.email || undefined, disabled: false }),
      telephone: this.formBuilder.control({ value: this.myCompany?.telephone || undefined, disabled: false }),
      webAddress: this.formBuilder.control({ value: this.myCompany?.webAddress || undefined, disabled: false }),
      // logo: this.formBuilder.group({ value: this.myCompany?.logo, disabled: true })
      logo: this.formBuilder.group({
        category: [undefined],
        description: [undefined],
        i18n: [undefined],
        index: [undefined],
        pimDimensionIds: [undefined],
        s3ETag: [undefined],
        s3Uri: [undefined],
        s3lastModifiedDate: [undefined],
        id: [undefined],
        name: [undefined],
        extra: [undefined]
      }),
      loginBackgroundImage: this.formBuilder.group({
        category: [undefined],
        description: [undefined],
        i18n: [undefined],
        index: [undefined],
        pimDimensionIds: [undefined],
        s3ETag: [undefined],
        s3Uri: [undefined],
        s3lastModifiedDate: [undefined],
        id: [undefined],
        name: [undefined],
        extra: [undefined]
      }),
      logoLogin: this.formBuilder.group({
        category: [undefined],
        description: [undefined],
        i18n: [undefined],
        index: [undefined],
        pimDimensionIds: [undefined],
        s3ETag: [undefined],
        s3Uri: [undefined],
        s3lastModifiedDate: [undefined],
        id: [undefined],
        name: [undefined],
        extra: [undefined]
      }),
      logoOrderConfirm: this.formBuilder.group({
        category: [undefined],
        description: [undefined],
        i18n: [undefined],
        index: [undefined],
        pimDimensionIds: [undefined],
        s3ETag: [undefined],
        s3Uri: [undefined],
        s3lastModifiedDate: [undefined],
        id: [undefined],
        name: [undefined],
        extra: [undefined]
      }),
      favicon: this.formBuilder.group({
        category: [undefined],
        description: [undefined],
        i18n: [undefined],
        index: [undefined],
        pimDimensionIds: [undefined],
        s3ETag: [undefined],
        s3Uri: [undefined],
        s3lastModifiedDate: [undefined],
        id: [undefined],
        name: [undefined],
        extra: [undefined]
      }),
      colors: this.formBuilder.group({
        primary: this.formBuilder.control({ value: this.myCompany?.colors?.primary || undefined, disabled: false }),
        secondary: this.formBuilder.control({ value: this.myCompany?.colors?.secondary || undefined, disabled: false }),
        hover: this.formBuilder.control({ value: this.myCompany?.colors?.hover || undefined, disabled: false }),
        contrasting: this.formBuilder.control({
          value: this.myCompany?.colors?.contrasting || undefined,
          disabled: false
        }),
        navbarcolor: this.formBuilder.control({ value: this.myCompany?.colors?.navbarcolor || undefined, disabled: false }),
        navbarfontcolor: this.formBuilder.control({ value: this.myCompany?.colors?.navbarfontcolor || undefined, disabled: false }),
        navbarfonthovercolor: this.formBuilder.control({ value: this.myCompany?.colors?.navbarfonthovercolor || undefined, disabled: false })
      }),
      extra: this.formBuilder.group({
        customization: this.formBuilder.group({
          indexHead: this.formBuilder.control({
            value: this.myCompany?.extra?.customization?.indexHead || undefined,
            disabled: false
          })
        }),
        WOFF2: this.formBuilder.group({
          category: [undefined],
          description: [undefined],
          i18n: [undefined],
          index: [undefined],
          pimDimensionIds: [undefined],
          s3ETag: [undefined],
          s3Uri: [undefined],
          s3lastModifiedDate: [undefined],
          id: [undefined],
          name: [undefined],
          extra: [undefined]
        }),
        WOFF: this.formBuilder.group({
          category: [undefined],
          description: [undefined],
          i18n: [undefined],
          index: [undefined],
          pimDimensionIds: [undefined],
          s3ETag: [undefined],
          s3Uri: [undefined],
          s3lastModifiedDate: [undefined],
          id: [undefined],
          name: [undefined],
          extra: [undefined]
        }),
        TTF: this.formBuilder.group({
          category: [undefined],
          description: [undefined],
          i18n: [undefined],
          index: [undefined],
          pimDimensionIds: [undefined],
          s3ETag: [undefined],
          s3Uri: [undefined],
          s3lastModifiedDate: [undefined],
          id: [undefined],
          name: [undefined],
          extra: [undefined]
        }),
        navbarBanner: this.formBuilder.group({
          backgroundColor: this.formBuilder.control({ value: '#000000', disabled: false }),
          textColor: this.formBuilder.control({ value: '#ffffff', disabled: false }),
          text: this.formBuilder.control({ value: '', disabled: false }),
          bannerHeight: this.formBuilder.control({ value: '40', disabled: false }),
          textFormatting: this.formBuilder.control({ value: '', disabled: false }),
          centerText: this.formBuilder.control({ value: false, disabled: false }),
          enabled: this.formBuilder.control({ value: false, disabled: false }),
          navbarBannerImage: this.formBuilder.group({
            category: [undefined],
            description: [undefined],
            i18n: [undefined],
            index: [undefined],
            pimDimensionIds: [undefined],
            s3ETag: [undefined],
            s3Uri: [undefined],
            s3lastModifiedDate: [undefined],
            id: [undefined],
            name: [undefined],
            extra: [undefined]
          })
        }),
        orderConfirmationExtra: this.formBuilder.group({
          billingAddress1: this.formBuilder.control({ value: this.myCompany?.extra?.orderConfirmationExtra?.billingAddress1 || undefined, disabled: false }),
          billingAddress2: this.formBuilder.control({ value: this.myCompany?.extra?.orderConfirmationExtra?.billingAddress2 || undefined, disabled: false }),
          billingAddress3: this.formBuilder.control({ value: this.myCompany?.extra?.orderConfirmationExtra?.billingAddress3 || undefined, disabled: false }),
          deliveryAddress1: this.formBuilder.control({ value: this.myCompany?.extra?.orderConfirmationExtra?.deliveryAddress1 || undefined, disabled: false }),
          deliveryAddress2: this.formBuilder.control({ value: this.myCompany?.extra?.orderConfirmationExtra?.deliveryAddress2 || undefined, disabled: false }),
          deliveryAddress3: this.formBuilder.control({ value: this.myCompany?.extra?.orderConfirmationExtra?.deliveryAddress3 || undefined, disabled: false }),
          bankTransfer: this.formBuilder.control({ value: this.myCompany?.extra?.orderConfirmationExtra?.bankTransfer || undefined, disabled: false }),
          companySeating: this.formBuilder.control({ value: this.myCompany?.extra?.orderConfirmationExtra?.companySeating || undefined, disabled: false }),
          numberVat: this.formBuilder.control({ value: this.myCompany?.extra?.orderConfirmationExtra?.numberVat || undefined, disabled: false }),
          textExtra: this.formBuilder.control({ value: this.myCompany?.extra?.orderConfirmationExtra?.textExtra || undefined, disabled: false })
        })
      })
    });

    this.form.patchValue({
      logo: {
        ...this.myCompany?.logo
      }
    });

    this.form.patchValue({
      logoLogin: {
        ...this.myCompany?.logoLogin
      }
    });

    //loginBackgroundImage
    if (this.myCompany?.loginBackgroundImage) {
      this.form.patchValue({
        loginBackgroundImage: {
          ...this.myCompany?.loginBackgroundImage
        }
      });
    }

    if (this.myCompany?.logoOrderConfirm) {
      this.form.patchValue({
        logoOrderConfirm: {
          ...this.myCompany?.logoOrderConfirm
        }
      });
    }

    if (this.myCompany?.favicon) {
      this.form.patchValue({
        favicon: {
          ...this.myCompany?.favicon
        }
      });
    } else if (this.myCompany?.extra?.favicon) {
      this.form.patchValue({
        favicon: {
          ...this.myCompany?.extra?.favicon
        }
      });
    }

    if (!this.myCompany?.extra) {
      this.myCompany = {
        ...(this.myCompany || ({} as any)),
        extra: {
          ...(this.myCompany?.extra || {})
        }
      };
    }

    if (this.myCompany?.extra.WOFF2) {
      this.form.get('extra').patchValue({
        WOFF2: {
          ...this.myCompany?.extra.WOFF2
        }
      });
    }
    if (this.myCompany?.extra.WOFF) {
      this.form.get('extra').patchValue({
        WOFF: {
          ...this.myCompany?.extra.WOFF
        }
      });
    }
    if (this.myCompany?.extra.TTF) {
      this.form.get('extra').patchValue({
        TTF: {
          ...this.myCompany?.extra.TTF
        }
      });
    }
    if (this.myCompany?.extra.navbarBanner) {
      this.form.get('extra').patchValue({
        navbarBanner: {
          ...this.myCompany?.extra.navbarBanner
        }
      });
    }
  }

  onSubmit(form?: FormGroup) {
    form.disable();

    if (form.invalid) {
      form.markAllAsTouched();
      form.enable();
      return;
    }

    const gungCompany: GungCompany = {
      ...(this.myCompany || {}),
      ...form.getRawValue(),
      extra: {
        ...(this.myCompany?.extra || {}),
        ...form.getRawValue().extra
      }
    };

    if (!gungCompany.logo?.s3Uri) {
      gungCompany.logo = undefined;
    }

    if (!gungCompany.logoLogin?.s3Uri) {
      gungCompany.logoLogin = undefined;
    }

    if (gungCompany.extra?.customization?.indexHead) {
      // gungCompany.extra.customization.indexHead = encodeURIComponent(gungCompany.extra.customization.indexHead);
    }

    if (gungCompany.favicon?.s3Uri) {
      gungCompany.extra.favicon = gungCompany.favicon;
    }

    this.companyService.postGungCompany(gungCompany).subscribe(
      result => {
        this.myCompany = result;
        form.enable();
        this.initForm();
        window.location.reload();
      },
      error => {
        console.log('MyCompanyComponent - onSubmit: ', error);
        form.enable();
      }
    );
  }

  save() {
    if (this.objectFileImage && Object.keys(this.objectFileImage).length > 0) {
      const images: File[] = [];
      const request: { [s: string]: Observable<any> } = {};
      for (const key of Object.keys(this.objectFileImage)) {
        if (this.objectFileImage[key]) {
          images.push(this.objectFileImage[key]);
          request[key] = this.uploadImages([this.objectFileImage[key]], key);
        }
      }
      forkJoin(request).subscribe(objectFileImage => {
        for (const key of Object.keys(objectFileImage)) {
          this.form.patchValue({
            key: objectFileImage[key]
          });
          this.form.get(key).patchValue(objectFileImage[key]);
        }
        this.onSubmit(this.form);
      });
      return;
    }

    if (this.objectFileFonts && Object.keys(this.objectFileFonts).length > 0) {
      const images: File[] = [];
      const request: { [s: string]: Observable<any> } = {};
      for (const key of Object.keys(this.objectFileFonts)) {
        if (this.objectFileFonts[key]) {
          images.push(this.objectFileFonts[key]);
          request[key] = this.uploadDoc([this.objectFileFonts[key]]);
        }
      }
      forkJoin(request).subscribe(objectFileFonts => {
        for (const key of Object.keys(objectFileFonts)) {
          this.form.get('extra').patchValue({
            key: objectFileFonts[key]
          });
          this.form.get('extra').get(key).patchValue(objectFileFonts[key]);
        }
        this.onSubmit(this.form);
      });
      return;
    }
    this.onSubmit(this.form);
  }

  onDropFont(files: File[], fontTag?: string) {
    // console.log('Drop', event);
    // event.preventDefault();
    // event.stopPropagation();
    // event.target.style.opacity = 1;
    // const files: File[] = event.target.files || event.dataTransfer.files;
    if (files.length > 0) {
      if (fontTag) {
        this.objectFileFonts[fontTag] = files[0];
        this.objectFileFontsPreview[fontTag] = undefined;
      }
      // event.target.value = '';
    }
  }

  onDragOver(event /* : DragEvent */) {
    // console.log(`Drag Over`, event);
    event.target.style.opacity = 0.5;
    event.preventDefault();
    event.stopPropagation();
  }

  onDragLeave(event /* : DragEvent */) {
    // console.log('Drag Leave', event);
    event.target.style.opacity = 1;
    event.preventDefault();
    event.stopPropagation();
  }

  onDrop(files: File[], imgTag?: string) {
    if (files.length > 0 && imgTag) {
      const file = files[0];
      const mimeType = file.type;

      const isImage = mimeType.match(/image\/*/) !== null;
      const isVideo = mimeType.match(/video\/*/) !== null;

      if (!isImage && !isVideo) {
        console.log('Only images are supported.');
        return;
      }

      this.objectFileImage[imgTag] = file;
      this.previewMedia(files, imgTag);
    }
  }

  previewMedia(files: File[], imgTag?: string) {
    if (!files || files.length === 0 || !imgTag) return;

    const file = files[0];
    const mimeType = file.type;
    const isImage = mimeType.match(/image\/*/) !== null;
    const isVideo = mimeType.match(/video\/*/) !== null;

    if (!isImage && !isVideo) return;

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const filePreview: S3Image = {
        category: 'Login Background',
        description: null,
        i18n: null,
        index: null,
        pimDimensionIds: null,
        s3ETag: null,
        s3Uri: reader.result as string,
        s3lastModifiedDate: new Date(file.lastModified),
        id: null,
        name: file.name,
        extra: { type: isVideo ? 'video' : 'image' }
      };
      this.objectFileImagePreview[imgTag] = filePreview;
    };
  }

  uploadImages(files: File[], imgTag?: string): Observable<any> {
    const imageObject = {
      index: 0,
      description: (imgTag || 'my company logo - ') + new Date().getTime(),
      category: imgTag || 'my company logo',
      s3Uri: 'images/no-image.jpg'
    };
    const formData = new FormData();
    formData.append('file', files[0]);
    formData.append('productImage', JSON.stringify(imageObject));
    return this.http.post<any>(`import/s3-image`, formData).pipe(
      map(image => {
        return image;
      })
    );
  }

  uploadDoc(files: File[], imgTag?: string): Observable<any> {
    return this.s3UploadService.postDocument(files[0]).pipe(
      map(doc => {
        return doc;
      })
    );
  }

  private uploadImagesLogo(images) {
    const timeStamp = new Date();
    if (!images || !images.length) {
      return of([]);
    }

    const promises = [];
    images.forEach(img => {
      if (!!img.file.$error) {
        return;
      }

      const formData = new FormData();
      formData.append('file', img.file);
      promises.push(this.http.post('json/logo-applicator/image', formData));
    });

    return forkJoin(promises).pipe(
      map(responseImages => {
        return responseImages.map(({ id, s3Uri }) => {
          return {
            id,
            uri: s3Uri
          };
        });
      })
    );
  }

  public setupCustomization() {
    const primary = this.form.get('colors').get('primary').value;
    if (!!primary) {
      let hsl = gungColorToHsl(primary);
      document.documentElement.style.setProperty('--dynamic-colour-primary', primary);
      document.documentElement.style.setProperty('--dynamic-colour-primary-h', hsl[0]);
      document.documentElement.style.setProperty('--dynamic-colour-primary-s', `${hsl[1]}%`);
      document.documentElement.style.setProperty('--dynamic-colour-primary-l', `${hsl[2]}%`);
    } else {
      document.documentElement.style.removeProperty('--dynamic-colour-primary');
      document.documentElement.style.removeProperty('--dynamic-colour-primary-h');
      document.documentElement.style.removeProperty('--dynamic-colour-primary-s');
      document.documentElement.style.removeProperty('--dynamic-colour-primary-l');
      if (this.primary) {
        const hsl = gungColorToHsl(primary);
        document.documentElement.style.setProperty('--dynamic-colour-primary', primary);
        document.documentElement.style.setProperty('--dynamic-colour-primary-h', hsl[0]);
        document.documentElement.style.setProperty('--dynamic-colour-primary-s', `${hsl[1]}%`);
        document.documentElement.style.setProperty('--dynamic-colour-primary-l', `${hsl[2]}%`);
      }
    }

    const secondary = this.form.get('colors').get('secondary').value;
    if (!!secondary) {
      const hsl = gungColorToHsl(secondary);
      document.documentElement.style.setProperty('--dynamic-colour-secondary', secondary);
      document.documentElement.style.setProperty('--dynamic-colour-secondary-h', hsl[0]);
      document.documentElement.style.setProperty('--dynamic-colour-secondary-s', `${hsl[1]}%`);
      document.documentElement.style.setProperty('--dynamic-colour-secondary-l', `${hsl[2]}%`);
    } else {
      document.documentElement.style.removeProperty('--dynamic-colour-secondary');
      document.documentElement.style.removeProperty('--dynamic-colour-secondary-h');
      document.documentElement.style.removeProperty('--dynamic-colour-secondary-s');
      document.documentElement.style.removeProperty('--dynamic-colour-secondary-l');
      if (this.secondary) {
        const hsl = gungColorToHsl(secondary);
        document.documentElement.style.setProperty('--dynamic-colour-secondary', secondary);
        document.documentElement.style.setProperty('--dynamic-colour-secondary-h', hsl[0]);
        document.documentElement.style.setProperty('--dynamic-colour-secondary-s', `${hsl[1]}%`);
        document.documentElement.style.setProperty('--dynamic-colour-secondary-l', `${hsl[2]}%`);
      }
    }

    const hover = this.form.get('colors').get('hover').value;
    if (!!hover) {
      const hsl = gungColorToHsl(hover);
      document.documentElement.style.setProperty('--dynamic-colour-hover', hover);
      document.documentElement.style.setProperty('--dynamic-colour-hover-h', hsl[0]);
      document.documentElement.style.setProperty('--dynamic-colour-hover-s', `${hsl[1]}%`);
      document.documentElement.style.setProperty('--dynamic-colour-hover-l', `${hsl[2]}%`);
    } else {
      document.documentElement.style.removeProperty('--dynamic-colour-hover');
      document.documentElement.style.removeProperty('--dynamic-colour-hover-h');
      document.documentElement.style.removeProperty('--dynamic-colour-hover-s');
      document.documentElement.style.removeProperty('--dynamic-colour-hover-l');
      if (this.hover) {
        const hsl = gungColorToHsl(hover);
        document.documentElement.style.setProperty('--dynamic-colour-hover', hover);
        document.documentElement.style.setProperty('--dynamic-colour-hover-h', hsl[0]);
        document.documentElement.style.setProperty('--dynamic-colour-hover-s', `${hsl[1]}%`);
        document.documentElement.style.setProperty('--dynamic-colour-hover-l', `${hsl[2]}%`);
      }
    }

    const contrasting = this.form.get('colors').get('contrasting').value;
    if (!!contrasting) {
      const hsl = gungColorToHsl(contrasting);
      document.documentElement.style.setProperty('--dynamic-colour-contrasting', contrasting);
      document.documentElement.style.setProperty('--dynamic-colour-contrasting-h', hsl[0]);
      document.documentElement.style.setProperty('--dynamic-colour-contrasting-s', `${hsl[1]}%`);
      document.documentElement.style.setProperty('--dynamic-colour-contrasting-l', `${hsl[2]}%`);
    } else {
      document.documentElement.style.removeProperty('--dynamic-colour-contrasting');
      document.documentElement.style.removeProperty('--dynamic-colour-contrasting-h');
      document.documentElement.style.removeProperty('--dynamic-colour-contrasting-s');
      document.documentElement.style.removeProperty('--dynamic-colour-contrasting-l');
      if (this.contrasting) {
        const hsl = gungColorToHsl(contrasting);
        document.documentElement.style.setProperty('--dynamic-colour-contrasting', contrasting);
        document.documentElement.style.setProperty('--dynamic-colour-contrasting-h', hsl[0]);
        document.documentElement.style.setProperty('--dynamic-colour-contrasting-s', `${hsl[1]}%`);
        document.documentElement.style.setProperty('--dynamic-colour-contrasting-l', `${hsl[2]}%`);
      }
    }
  }

  shouldDisplayButtons(): boolean {
    // We don't want to display the buttons when we are on the configuration wrapper.
    // The reason for this is that it is composed of smaller, more specific components that
    // are managed on their own. Saving on this top level will not be able to save changes done there
    // so removing it entirely makes more sense in a UX perspective.
    if (!!this.selectedNavItem && this.selectedNavItem === 'adminConfigurationWrapper') {
      return false;
    }
    if (!!this.selectedNavItem && this.selectedNavItem === 'adminCompanyTemplates') {
      return false;
    }
    return true;
  }

  removeImage(imgTag: string) {
    this.objectFileImagePreview[imgTag] = null
    delete this.objectFileImage[imgTag]

    const control = this.form.get(imgTag)
    if (control) {
      control.patchValue({
        category: undefined,
        description: undefined,
        i18n: undefined,
        index: undefined,
        pimDimensionIds: undefined,
        s3ETag: undefined,
        s3Uri: undefined,
        s3lastModifiedDate: undefined,
        id: undefined,
        name: undefined,
        extra: undefined
      })
    }
  }
}
